import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { withProps } from 'recompose';

/**
 * @typedef {import('@apollo/client/react/hooks/useMutation').useMutation.MutationFunction<any, any>} MutationFunction
 */

/**
 * @typedef {import('@apollo/client/react/hooks/useQuery').useQuery.Options} QueryOptions

/**
 * @typedef {Object} WithMutationOptions
 * @property {string} [name] - The prop name to inject the mutate function as (defaults to 'mutate')
 * @property {(mutate: MutationFunction) => any} [fn] - Optional wrapper to transform the mutate function before injecting it
 */

/**
 * @param {any} [query]
 * @param {QueryOptions | ((props: any) => QueryOptions)} [options]
 * @param {string} [name]
 */
const withQuery = (query, options, name) =>
  withProps((props) => {
    let queryOptions = typeof options === 'function' ? options(props) : options;

    const { data: queryResult, ...otherQueryData } = useQuery(
      query,
      queryOptions,
    );
    return {
      [name || 'data']: {
        ...props.data,
        ...otherQueryData,
        ...queryResult,
      },
      ...queryResult,
    };
  });

/**
 * @param {any} query
 * @param {import('@apollo/client/react/hooks/useMutation').useMutation.Options} [options]
 * @param {WithMutationOptions} [mutateOptions]
 */
const withMutation = (query, options, mutateOptions) =>
  withProps((props) => {
    let queryOptions = options;

    if (options instanceof Function) {
      queryOptions = options(props);
    }

    const [mutate, otherProps] = useMutation(query, queryOptions);
    const mutateFunctionName = mutateOptions?.name || 'mutate';
    const mutateFunction = mutateOptions?.fn
      ? mutateOptions?.fn(mutate)
      : mutate;

    const data = {
      ...props.data,
      loading: props?.data?.loading || otherProps?.loading,
      error: props?.data?.error || otherProps?.error,
    };

    return {
      data,
      [mutateFunctionName]: mutateFunction,
    };
  });

const withApolloCLient = withProps(() => ({
  client: useApolloClient(),
}));

export { withQuery, withMutation, withApolloCLient };
